import { useEffect } from 'react';

import { useToggle } from '../../hooks/useToggle';
import { sizes } from '../../tokens/sizes';
import { BaseItem, Item } from '../../types/Item';
import { blurOnClick } from '../../utils/event';
import { Layout } from '../Layout/Layout';
import { AccordionItemHeader } from './AccordionItemHeader';
import { BorderMode, PaddingMode } from './types';

interface Props<D = unknown> {
  disableContentPadding?: boolean;
  item: Item<D>;
  border?: BorderMode;
  borderRadius?: 's' | 'm';
  chipPosition?: 'left' | 'right';
  mode?: 'default' | 'compact';
  isIndented?: boolean;
  mb?: number;
  padding?: PaddingMode;
  togglePosition?: 'left' | 'right';
  search?: string;
  onToggle?: (isExpanded: boolean, item: BaseItem<D>) => void;
}

export const AccordionItem = <D,>({
  disableContentPadding = false,
  border = 'all',
  borderRadius,
  item,
  isIndented = false,
  mb,
  mode = 'default',
  padding = 'all',
  togglePosition,
  onToggle,
  search,
  chipPosition = 'left',
}: Props<D>) => {
  const { content, details, isExpanded = false, isLoading, title } = item;

  const [isVisible, toggle, on, off] = useToggle(isExpanded);

  const headerStyle =
    mode === 'compact'
      ? {
          bg: 'background.quiet',
          borderRadius: 'm' as const,
          minHeight: '40px',
        }
      : {};

  useEffect(() => {
    if (!isVisible && isExpanded) {
      on();
    } else if (isVisible && !isExpanded) {
      off();
    }
  }, [isExpanded]);

  const handleAccordionHeaderClick = (event: MouseEvent) => {
    onToggle?.(!isVisible, item);
    toggle();
    blurOnClick(event);
    event.preventDefault();
  };

  const borderX = border === 'all' ? 'border.divider' : 'none';
  const borderY = ['all', 'vertical'].includes(border)
    ? 'border.divider'
    : 'none';
  const p = padding === 'all' ? 4 : 0;

  return (
    <Layout
      as="details"
      bg="background"
      borderRadius={borderRadius}
      borderBottom={borderY}
      borderLeft={borderX}
      borderRight={borderX}
      borderTop={borderY}
      direction="column"
      open={isVisible && !isLoading}
      mb={mb}
    >
      <AccordionItemHeader
        bg={headerStyle.bg}
        borderRadius={headerStyle.borderRadius}
        isVisible={isVisible && !isLoading}
        item={item}
        minH={headerStyle.minHeight}
        padding={padding}
        search={search}
        togglePosition={togglePosition}
        onClick={handleAccordionHeaderClick}
        chipPosition={chipPosition}
      />
      <Layout
        as="section"
        aria-labelledby={`${title}-header`}
        direction="column"
        display={isVisible ? undefined : 'none'}
        id={`${title}-panel`}
        ml={isIndented ? `${sizes['icon.m'] + 8}px` : undefined} // indent with: icon size + spacing
        pb={p}
        px={disableContentPadding ? 0 : p}
      >
        {details}
        {content}
      </Layout>
    </Layout>
  );
};
